import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { LanguagePicker, useLanguage } from '../i18n/LanguageContext.jsx';
import { tk } from '../i18n/translate.js';
import AccountChrome, { SIGN_IN_PAGE_ID, useSignOut } from './AccountChrome.jsx';
import { APP_NAME } from '../config/index.js';
import { DEFAULT_PAGE_ID, MENUS, getPageById, routeForPageId } from './menuData.js';
import { canOpen, roleLabel } from '../config/routeAccess.js';
import { useFieldSync } from '../offline/useFieldSync.js';
import '../offline/field.scss';

// Mobile bottom tab bar, app style: Home, one role tab, Scan (raised hero), one role tab, then More - always five slots.
const TAB = {
  home: { key: 'home', pageId: DEFAULT_PAGE_ID, icon: 'fa-house', label: tk('Home') },
  scan: { key: 'scan', route: '/scan', icon: 'fa-qrcode', label: tk('Scan'), hero: true },
  sync: { key: 'sync', route: '/sync-status', icon: 'fa-sync-alt', label: tk('Sync'), syncBadge: true },
  audit: { key: 'audit', route: '/physical-audit-verification', icon: 'fa-clipboard-check', label: tk('Audit') },
  stock: { key: 'stock', route: '/stock-points-par-management', icon: 'fa-layer-group', label: tk('Stock') },
  assets: { key: 'assets', route: '/inwarding-asset-tagging', icon: 'fa-boxes-stacked', label: tk('Assets') },
  orders: { key: 'orders', route: '/work-orders-repair-management', icon: 'fa-wrench', label: tk('Orders') },
  locations: { key: 'locations', route: '/property-location-registry', icon: 'fa-building', label: tk('Locations') },
  catalogue: { key: 'catalogue', route: '/master-item-catalogue', icon: 'fa-book', label: tk('Catalogue') },
  custody: { key: 'custody', route: '/asset-custody-movements', icon: 'fa-people-arrows', label: tk('Custody') },
  books: { key: 'books', route: '/depreciation-runs-gl-schedules', icon: 'fa-calculator', label: tk('Books') },
  disposals: { key: 'disposals', route: '/asset-disposals-write-offs', icon: 'fa-trash-can', label: tk('Disposals') },
  reconcile: { key: 'reconcile', route: '/audit-reconciliation-console', icon: 'fa-scale-balanced', label: tk('Reconcile') },
  exceptions: { key: 'exceptions', route: '/audit-exception-queue', icon: 'fa-triangle-exclamation', label: tk('Exceptions') },
  contents: { key: 'contents', route: '/location-contents-sheet', icon: 'fa-list-check', label: tk('Contents') },
};

// [left tab, right tab] per role; Scan sits between them. Auditors are read-only and never scan, so their
// centre slot is a plain tab instead of the hero.
const ROLE_TABS = {
  field_user: ['audit', 'sync'],
  storekeeper: ['stock', 'sync'],
  admin: ['assets', 'orders'],
  customer_admin: ['catalogue', 'orders'],
  property_manager: ['locations', 'orders'],
  department_head: ['custody', 'orders'],
  finance_controller: ['books', 'disposals'],
  auditor: ['reconcile', 'exceptions', 'contents'],
};
// Used to fill a slot when the role's preferred tab is not open to it.
const FILL_TABS = ['orders', 'contents', 'custody', 'sync'];

const tabRoute = (tb) => tb.route || routeForPageId(tb.pageId);

/** Home + up to three role tabs (Scan hero in the middle when the role may scan), each one the role can open. */
function tabsForRole(role) {
  const ok = (tb) => canOpen(role, tabRoute(tb));
  const hero = role !== 'auditor' && ok(TAB.scan);
  const keys = [...new Set([...(ROLE_TABS[role] || []), ...FILL_TABS])];
  const picked = keys.map((k) => TAB[k]).filter(ok).slice(0, hero ? 2 : 3);
  const middle = hero ? [picked[0], TAB.scan, picked[1]] : picked;
  return [TAB.home, ...middle.filter(Boolean)];
}

const SHEET_LABELS = {
  99701: tk('Scan'),
  99702: tk('Quick View'),
  99703: tk('Sync'),
  73948: tk('Dashboard'),
  73938: tk('Locations'),
  73939: tk('Room Kits'),
  73940: tk('Inwarding'),
  73941: tk('Custody'),
  73942: tk('Audit Scan'),
  73943: tk('Reconcile'),
  99001: tk('Schedules'),
  99002: tk('Blind Counts'),
  73944: tk('Incidents'),
  73945: tk('Work Orders'),
  99003: tk('Depreciation'),
  73947: tk('Disposals'),
  73936: tk('Directory'),
  73937: tk('Security'),
  99004: tk('Plans'),
  99005: tk('Integrations'),
  99006: tk('Catalogue'),
  99007: tk('Tag Studio'),
  99008: tk('First Scan'),
  99009: tk('Stock & Par'),
  99010: tk('Event Kits'),
  99011: tk('Loaners'),
};

const itemRoute = (it) => it.route || routeForPageId(it.pageId);

/** The menu as this role may use it: leaves the role cannot open are dropped, and so are groups left empty. */
function menuForRole(items, role) {
  return items
    .map((it) => (Array.isArray(it.children) ? { ...it, children: menuForRole(it.children, role) } : it))
    .filter((it) => (Array.isArray(it.children) ? it.children.length > 0 : canOpen(role, itemRoute(it))));
}

function flattenLeaves(items, out = []) {
  items.forEach((it) => {
    if (it.children && it.children.length) flattenLeaves(it.children, out);
    else if (it.pageId) out.push(it);
  });
  return out;
}

function DesktopMenu({ items, level = 0 }) {
  const { t } = useLanguage();
  return (
    <ul className={level === 0 ? 'navbar-nav' : 'dropdown-menu'} aria-labelledby={undefined}>
      {items.map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        if (hasChildren) {
          const id = `mainMenu-${item.label.replace(/\W+/g, '_')}`;
          return (
            <li key={item.label} className="nav-item dropdown">
              <a
                className={`${level === 0 ? 'nav-link text-white' : 'dropdown-item'} dropdown-toggle`}
                href="#"
                id={id}
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                onClick={(e) => e.preventDefault()}
              >
                {item.icon && <i className={`${item.icon} me-1`} aria-hidden="true"></i>}
                {t(item.label)}
              </a>
              <DesktopMenu items={item.children} level={level + 1} />
            </li>
          );
        }
        return (
          <li key={item.label} className="nav-item">
            <NavLink to={item.route || routeForPageId(item.pageId)} className={level === 0 ? 'nav-link text-white' : 'dropdown-item'}>
              {item.icon && <i className={`${item.icon} me-1`} aria-hidden="true"></i>}
              {t(item.label)}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function BottomSheet({ id, open, onClose, title, subtitle, icon, children }) {
  const { t } = useLanguage();
  return (
    <>
      <div className={`native-sheet-backdrop${open ? ' show' : ''}`} role="presentation" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}></div>
      <div
        className={`native-bottom-sheet${open ? ' show' : ''}`}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-hidden={!open}
      >
        <div className="sheet-drag-pill"></div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h6 className="mb-0 fw-bold text-dark">
              <i className={`fas ${icon} text-primary me-2`} aria-hidden="true"></i>
              {title}
            </h6>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
          <button type="button" className="btn-close" aria-label={t('Close')} onClick={onClose}></button>
        </div>
        {children}
      </div>
    </>
  );
}

/**
 * Shared application shell (desktop top bar, mobile app bar, bottom tab bar, "More" sheet).
 * Every page shows the same navigation, filtered to what the signed-in role may open (the design's per-layout
 * `menuKey` subsets made the menu change from page to page, so it is no longer used to pick the menu).
 */
// eslint-disable-next-line no-unused-vars
export default function AppShell({ menuKey = 'full', children }) {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const signOut = useSignOut();
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [standing, setStanding] = useState(null);
  const { pathname } = useLocation();
  const menu = menuForRole(MENUS.full, user?.role);
  const leaves = flattenLeaves(menu);
  const inMenu = (id) => leaves.some((l) => l.pageId === id);
  const tabs = tabsForRole(user?.role);
  // Offline queue state for the Sync tab badge and the offline indicator.
  const fieldSync = useFieldSync();
  const syncBadge = fieldSync.counts.pending + fieldSync.counts.conflicts + fieldSync.counts.failed;
  const homeRoute = routeForPageId(DEFAULT_PAGE_ID);

  // Billing ladder: customer admins see the +7 / +14 / +21 notice on every page, not just the plans page.
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'customer_admin') {
      setStanding(null);
      return undefined;
    }
    let live = true;
    api.getAccountStanding().then((s) => live && setStanding(s)).catch(() => live && setStanding(null));
    return () => {
      live = false;
    };
  }, [isAuthenticated, user?.role, pathname]);

  // Header context: the signed-in user's current property and its active maintenance incidents (bell badge).
  const [shellContext, setShellContext] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) {
      setShellContext(null);
      return undefined;
    }
    let live = true;
    api
      .getMaintenanceOverview()
      .then((o) => live && setShellContext({ property: o.property || null, activeIncidents: o.kpis?.active ?? 0 }))
      .catch(() => live && setShellContext(null));
    return () => {
      live = false;
    };
  }, [isAuthenticated, user?.id, pathname]);
  const propertyName = shellContext?.property?.name || '';
  const activeIncidents = shellContext?.activeIncidents || 0;

  useEffect(() => {
    document.body.classList.add('app-chrome');
    return () => document.body.classList.remove('app-chrome');
  }, []);

  useEffect(() => {
    document.body.style.overflow = moreOpen || accountOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [moreOpen, accountOpen]);

  const role = (user && user.role) || '';

  return (
    <>
      {/* Desktop top bar (> 768px) */}
      <nav className="navbar navbar-expand-xxl navbar-dark desktop-navbar fixed-top">
        <div className="container-fluid">
          <Link className="navbar-brand d-flex align-items-center" to={homeRoute}>
            <i className="fas fa-hotel me-2" aria-hidden="true"></i>
            <div className="d-inline-block">{APP_NAME}</div>
          </Link>
          {/* Collapsed (< 1400px): brand, account cluster and toggler share the top row; the menu opens below. */}
          <button
            className="navbar-toggler order-2 ms-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label={t('Toggle navigation')}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse order-3 order-xxl-1" id="navbarNav">
            <DesktopMenu items={menu} />
          </div>
          <div className="ms-auto d-flex align-items-center gap-2 order-1 order-xxl-2">
            {propertyName && (
              <span className="badge bg-white text-primary px-2 py-1 shell-property-badge" data-testid="shell-property" title={propertyName}>
                <i className="fas fa-map-marker-alt me-1" aria-hidden="true"></i>{propertyName}
              </span>
            )}
            <AccountChrome variant="desktop" />
          </div>
        </div>
      </nav>

      {/* Mobile top app bar (<= 768px) */}
      <header className="mobile-only-chrome mobile-app-header">
        <div className="d-flex align-items-center gap-2">
          <Link to={homeRoute} className="mobile-brand-title">
            <i className="fas fa-hotel" aria-hidden="true"></i>
            <span>{APP_NAME}</span>
          </Link>
          {isAuthenticated && (
            <button type="button" className="mobile-role-badge text-capitalize" onClick={() => setAccountOpen(true)} title={t('Account')}>
              <i className="fas fa-user-circle" aria-hidden="true"></i>
              <span>{t(roleLabel(role))}</span>
              <i className="fas fa-caret-down" style={{ fontSize: 9 }} aria-hidden="true"></i>
            </button>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {isAuthenticated && !fieldSync.online && (
            <Link to="/sync-status" className="badge rounded-pill bg-warning text-dark text-decoration-none" id="shell-offline-badge" title={t('Offline: actions are saved on this device and synced later')}>
              <i className="fas fa-wifi me-1" aria-hidden="true"></i>
              {t('Offline')}
            </Link>
          )}
          {inMenu(73944) && (
            <Link
              to={routeForPageId(73944)}
              className="text-white position-relative text-decoration-none p-1"
              title={t('Maintenance Incidents')}
              aria-label={activeIncidents ? t('Maintenance incidents: {count} active', { count: activeIncidents }) : t('Maintenance incidents')}
            >
              <i className="fas fa-bell" style={{ fontSize: 17 }} aria-hidden="true"></i>
              {activeIncidents > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 9, padding: '2px 4px' }} data-testid="shell-incident-count">
                  {activeIncidents > 99 ? '99+' : activeIncidents}
                </span>
              )}
            </Link>
          )}
          {isAuthenticated ? (
            <button type="button" className="btn p-0 border-0 ms-1" onClick={() => setAccountOpen(true)} aria-label={t('Account')}>
              <AvatarMini user={user} />
            </button>
          ) : (
            <Link to={routeForPageId(SIGN_IN_PAGE_ID)} className="mobile-role-badge">
              <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
              <span>{t('Log in')}</span>
            </Link>
          )}
        </div>
      </header>

      {/* Main content */}
      <main id="main-content" className="container-fluid px-2 px-md-4">
        {standing?.banner && pathname !== '/account-subscription-plans' && (
          <div className={`alert alert-${standing.banner.level} rounded-0 mb-0 py-2 small`} role="alert" id="billing-standing-banner">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {standing.banner.message} <Link to="/account-subscription-plans">{t('Review billing')}</Link>
          </div>
        )}
        {children ?? <Outlet />}
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="mobile-only-chrome native-mobile-tab-bar" id="nativeTabBar" aria-label={t('Mobile App Navigation')}>
        {tabs.map((tb) =>
          tb.hero ? (
            <NavLink key={tb.key} to={tabRoute(tb)} className="native-tab-hero" aria-label={t('Camera Scan HUD')}>
              <div className="hero-scan-bubble">
                <i className={`fas ${tb.icon}`} aria-hidden="true"></i>
              </div>
              <span className="hero-scan-label">{t(tb.label)}</span>
            </NavLink>
          ) : (
            <NavLink key={tb.key} to={tabRoute(tb)} className={({ isActive }) => `native-tab-link${isActive ? ' active' : ''}`}>
              <i className={`fas ${tb.icon} native-tab-icon`} aria-hidden="true"></i>
              {tb.syncBadge && syncBadge > 0 && (
                <span className="native-tab-badge" data-testid="tab-sync-badge" aria-label={t('{count} waiting to sync', { count: syncBadge })}>
                  {syncBadge > 99 ? '99+' : syncBadge}
                </span>
              )}
              <span className="native-tab-label">{t(tb.label)}</span>
            </NavLink>
          ),
        )}
        <button type="button" className="native-tab-link" onClick={() => setMoreOpen(true)}>
          <i className="fas fa-bars native-tab-icon" aria-hidden="true"></i>
          <span className="native-tab-label">{t('More')}</span>
        </button>
      </nav>

      {/* Account sheet (replaces the design's persona picker with the real session) */}
      <BottomSheet
        id="accountSheetModal"
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        title={t('Account')}
        subtitle={user ? user.email : undefined}
        icon="fa-user-tag"
      >
        {user && (
          <div className="list-group list-group-flush mt-2">
            <div className="list-group-item d-flex align-items-center gap-3 py-3">
              <div className="rounded-circle p-2 bg-primary-subtle text-primary">
                <i className="fas fa-user fa-lg" aria-hidden="true"></i>
              </div>
              <div>
                <div className="fw-bold text-dark">{user.name || user.email}</div>
                <small className="text-muted text-capitalize">{t(roleLabel(role))}</small>
              </div>
            </div>
            <div className="list-group-item py-3">
              <label htmlFor="language-picker-sheet" className="form-label small text-muted mb-1">
                <i className="fas fa-language me-2" aria-hidden="true"></i>
                {t('Language')}
              </label>
              <LanguagePicker id="language-picker-sheet" />
            </div>
            <button
              type="button"
              className="list-group-item list-group-item-action d-flex align-items-center gap-3 py-3 text-danger"
              onClick={() => {
                setAccountOpen(false);
                signOut();
              }}
            >
              <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
              <span className="fw-bold">{t('Sign out')}</span>
            </button>
          </div>
        )}
      </BottomSheet>

      {/* More sheet: flat module grid */}
      <BottomSheet
        id="moreSheetModal"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        title={t('All {app} Modules', { app: APP_NAME })}
        subtitle={shellContext?.property ? `${shellContext.property.name} · ${shellContext.property.code}` : undefined}
        icon="fa-layer-group"
      >
        <div className="sheet-grid-nav">
          {leaves.map((l) => (
            <Link key={l.pageId} to={l.route || routeForPageId(l.pageId)} className="sheet-grid-item" onClick={() => setMoreOpen(false)}>
              <i className={l.icon} aria-hidden="true"></i>
              <span>{t(SHEET_LABELS[l.pageId] || (getPageById(l.pageId) || {}).name || l.label)}</span>
            </Link>
          ))}
          {isAuthenticated ? (
            <button
              type="button"
              className="sheet-grid-item"
              style={{ color: '#B91C1C', background: '#F8FAFC' }}
              onClick={() => {
                setMoreOpen(false);
                signOut();
              }}
            >
              <i className="fas fa-sign-out-alt" style={{ color: '#B91C1C' }} aria-hidden="true"></i>
              <span>{t('Sign Out')}</span>
            </button>
          ) : (
            <Link to={routeForPageId(SIGN_IN_PAGE_ID)} className="sheet-grid-item" onClick={() => setMoreOpen(false)}>
              <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
              <span>{t('Log In')}</span>
            </Link>
          )}
        </div>
      </BottomSheet>
    </>
  );
}

function AvatarMini({ user }) {
  const initials = ((user && (user.name || user.email)) || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
  return (
    <span
      className="rounded-circle border border-white d-inline-flex align-items-center justify-content-center bg-white text-primary fw-bold"
      style={{ width: 30, height: 30, fontSize: 11 }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
